import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Archive, Download, ShieldCheck, Send, Copy, Loader2, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status }) {
  const map = {
    draft: "border-white/10 text-gray-300",
    sent_for_signature: "border-yellow-500/40 text-yellow-400",
    signed: "border-emerald-500/40 text-emerald-400",
    archived: "border-[#C9A227]/40 gold-text",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${map[status] || map.draft}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function ArchivesList() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api
      .listContracts(filter || undefined)
      .then(setContracts)
      .catch((err) => toast.error("Failed to load archives", { description: err.message }))
      .finally(() => setLoading(false));
  }, [filter]);

  const filters = [
    { key: "", label: "All" },
    { key: "draft", label: "Drafts" },
    { key: "sent_for_signature", label: "Awaiting" },
    { key: "signed", label: "Signed" },
    { key: "archived", label: "Archived" },
  ];

  return (
    <div className="space-y-8" data-testid="archives-list">
      <section>
        <div className="flex items-center gap-3 mb-3">
          <Archive className="w-5 h-5 gold-text" strokeWidth={1.5} />
          <span className="label-tag">Imperial Archives</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
          The <span className="gold-text">Vault of Seals</span>.
        </h1>
        <p className="text-gray-400 mt-3 max-w-2xl">
          Every forged instrument. Every handshake. Sealed in encrypted PDF and mirrored to the Imperial Vault.
        </p>
      </section>

      <div className="divider-gold" />

      <div className="flex flex-wrap gap-2" data-testid="archive-filters">
        {filters.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold border transition-all ${
              filter === f.key
                ? "border-[#C9A227] gold-text bg-[#C9A227]/5"
                : "border-white/10 text-gray-400 hover:border-white/30 hover:text-white"
            }`}
            data-testid={`filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Opening the Vault…
        </div>
      ) : contracts.length === 0 ? (
        <div className="card-sharp p-12 text-center" data-testid="archives-empty">
          <Archive className="w-10 h-10 gold-text mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-gray-400">No contracts in this section of the Vault.</p>
          <Link to="/forge" className="btn-gold inline-flex items-center gap-2 mt-6">
            Open the Forge
          </Link>
        </div>
      ) : (
        <div className="card-sharp overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-gray-500">
                <th className="px-6 py-4">Code</th>
                <th className="px-6 py-4">Template</th>
                <th className="px-6 py-4">Counterparty</th>
                <th className="px-6 py-4">Admiral</th>
                <th className="px-6 py-4">Gold</th>
                <th className="px-6 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={() => (window.location.href = `/archives/${c.id}`)}
                  data-testid={`archive-row-${c.id}`}
                >
                  <td className="px-6 py-4 font-mono text-xs gold-text">{c.contract_code}</td>
                  <td className="px-6 py-4 font-bold">{c.template_name}</td>
                  <td className="px-6 py-4 text-gray-300">{c.counterparty}</td>
                  <td className="px-6 py-4 text-gray-300">{c.admiral_name}</td>
                  <td className="px-6 py-4 font-mono gold-text">{(c.gold_amount || 0).toLocaleString()}</td>
                  <td className="px-6 py-4"><StatusPill status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContractDetail({ id }) {
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .getContract(id)
      .then(setContract)
      .catch((err) => toast.error("Not found", { description: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleHandshake = async () => {
    setActing(true);
    try {
      const updated = await api.initiateHandshake(id);
      setContract(updated);
      toast.success("Digital Handshake initiated", {
        description: "Tokenized signing link generated.",
      });
    } catch (err) {
      toast.error("Handshake failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setActing(false);
    }
  };

  const handleArchive = async () => {
    setActing(true);
    try {
      const updated = await api.archiveContract(id);
      setContract(updated);
      toast.success("Sealed in the Imperial Vault");
    } catch (err) {
      toast.error("Archive failed", { description: err.message });
    } finally {
      setActing(false);
    }
  };

  const copySignLink = () => {
    if (!contract?.sign_token) return;
    const link = `${window.location.origin}/sign/${contract.sign_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Signing link copied", { description: link });
  };

  if (loading || !contract) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500" data-testid="contract-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-3" /> Opening the seal…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="contract-detail">
      <button
        onClick={() => navigate("/archives")}
        className="inline-flex items-center gap-2 text-xs label-tag hover:text-white transition-colors"
        data-testid="back-to-archives"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Archives
      </button>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-2">
          <div className="label-tag">{contract.template_name}</div>
          <h1 className="text-4xl font-black tracking-tighter">{contract.counterparty}</h1>
          <div className="font-mono text-xs gold-text">{contract.contract_code}</div>
          <div className="flex items-center gap-3 pt-2">
            <StatusPill status={contract.status} />
            <span className="text-xs text-gray-500">
              Forged {new Date(contract.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="card-sharp p-5 space-y-3" data-testid="contract-actions">
          <a
            href={api.contractPdfUrl(contract.id)}
            target="_blank"
            rel="noreferrer"
            className="btn-gold w-full inline-flex items-center justify-center gap-2"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>

          {contract.status === "draft" && (
            <button
              onClick={handleHandshake}
              disabled={acting}
              className="btn-ghost-gold w-full inline-flex items-center justify-center gap-2"
              data-testid="initiate-handshake-btn"
            >
              <Send className="w-4 h-4" /> Initiate Digital Handshake
            </button>
          )}

          {contract.sign_token && contract.status !== "signed" && (
            <button
              onClick={copySignLink}
              className="btn-ghost-gold w-full inline-flex items-center justify-center gap-2"
              data-testid="copy-sign-link-btn"
            >
              <Copy className="w-4 h-4" /> Copy Signing Link
            </button>
          )}

          {contract.status === "signed" && (
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 text-center">
              <ShieldCheck className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
              <div className="text-xs label-tag text-emerald-400">Sealed</div>
              <div className="text-sm font-bold mt-1">{contract.signed_by}</div>
              <div className="text-xs text-gray-500 mt-1">
                {new Date(contract.signed_at).toLocaleString()}
              </div>
            </div>
          )}

          {contract.status === "signed" && (
            <button
              onClick={handleArchive}
              disabled={acting}
              className="btn-ghost-gold w-full inline-flex items-center justify-center gap-2"
              data-testid="archive-btn"
            >
              <Archive className="w-4 h-4" /> Seal in Vault
            </button>
          )}
        </div>
      </section>

      <div className="card-sharp p-8" data-testid="contract-body">
        <div className="label-tag mb-4">Instrument Body</div>
        <pre className="contract-body">{contract.body}</pre>
      </div>
    </div>
  );
}

export default function Archives() {
  const { id } = useParams();
  return id ? <ContractDetail id={id} /> : <ArchivesList />;
}
